import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CompanyRoles, RoleDefinitionDto } from "auth";

import { UpdateRoleDefinitionPayload } from "lib/dto";
import { useCurrentCompany } from "ui/context/auth";
import { useRestApi } from "ui/context/restApi";

import { queryKeys } from "./queryKeys";

export const useRolesQuery = () => {
  const { getRoles } = useRestApi().roles;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.ROLE_DEFINITIONS, currentCompany.id],
    queryFn: () => getRoles(currentCompany.id),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useRoleDetailsQuery = (id: string) => {
  const { getRoleDefinition } = useRestApi().roles;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.ROLE_DEFINITION_DETAILS, id, currentCompany.id],
    queryFn: () => getRoleDefinition(id, currentCompany.id),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const usePermissionsQuery = () => {
  const { getPermissions } = useRestApi().roles;

  return useQuery({
    queryKey: [queryKeys.PERMISSIONS],
    queryFn: getPermissions,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

// MUTATIONS
export const useCreateRoleMutation = () => {
  const { createRole } = useRestApi().roles;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateRoleDefinitionPayload) => createRole(payload, currentCompany.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ROLE_DEFINITIONS, currentCompany.id],
        refetchType: "active",
      });
    },
  });
};

export const useUpdateRoleMutation = (id: string) => {
  const { updateRole } = useRestApi().roles;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateRoleDefinitionPayload) =>
      updateRole(payload, id, currentCompany.id),
    onSuccess: (result) => {
      queryClient.setQueryData<CompanyRoles>([queryKeys.ROLE_DEFINITIONS], (old) => {
        if (!old) {
          return;
        }
        const roles = old?.roles.map((role) => {
          if (role.id === result.id) {
            return { ...role, ...result };
          }
          return role;
        });
        return { ...old, roles };
      });
      queryClient.setQueryData<RoleDefinitionDto>(
        [queryKeys.ROLE_DEFINITION_DETAILS, id],
        () => result,
      );
    },
  });
};
export const useRolePermissionsMutation = (id: string) => {
  const { updateRole } = useRestApi().roles;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateRoleDefinitionPayload) =>
      updateRole(payload, id, currentCompany.id),
    onSuccess: (result) => {
      queryClient.setQueryData<RoleDefinitionDto>(
        [queryKeys.ROLE_DEFINITION_DETAILS, id],
        () => result,
      );
    },
  });
};
